import * as Popover from "@radix-ui/react-popover";
import { popoverContent, column, columnTitle } from "./SidebarPopoverMenu.css";
import React, { useMemo, useState } from "react";
import { vars } from "@puzzle/theme";
import NextLink from "next/link";
import { useAddReviewMenuColumns, AddReviewMenuItem } from "./useAddReviewMenuColumns";
import { Button } from "@puzzle/ve";
import { Tooltip } from "@puzzle/ui";
import { Hamburger } from "@puzzle/icons";
import { useNavSidebarState } from "domains/app/components/MinimizeSidebarButton/MinimizeSidebarButton";
import { IS_CLIENT } from "lib/config";
import { MODAL_ROOT_ID } from "constants/ui";
import { useLinkDisplay } from "./useLinkDisplay";
import {
  ROUTE_ACCESS_STATUS_MESSAGES,
  LinkDisplayStatus,
} from "components/layout/private/SidebarV2/linkDisplayStatus";
import { zIndex } from "@puzzle/utils";
import { addRemoveItemStyle } from "./navItems.css";
import Analytics from "lib/analytics/analytics";

type MenuItemProps = {
  item: AddReviewMenuItem;
  onClose: () => void;
};

const MenuItem = ({ item, onClose }: MenuItemProps) => {
  const { getLinkDisplayStatus } = useLinkDisplay();
  if ("link" in item) {
    const linkDisplayStatus = getLinkDisplayStatus(item.link);
    // Don't render feature-flagged items at all
    if (linkDisplayStatus === LinkDisplayStatus.POSTHOG_FEATURE_FLAGGED) {
      return null;
    }

    const isDisabled = linkDisplayStatus !== LinkDisplayStatus.SHOW;
    const disabledMessage = ROUTE_ACCESS_STATUS_MESSAGES[linkDisplayStatus];

    const menuItem = (
      <NextLink
        href={item.link}
        className={addRemoveItemStyle}
        aria-disabled={isDisabled}
        onClick={() => {
          if (isDisabled) {
            return;
          }
          Analytics.sidebarLinkClicked({
            sidebarLocation: "addReviewMenu",
            link: item.link,
          });
          onClose();
        }}
      >
        {item.label}
      </NextLink>
    );

    if (isDisabled) {
      return (
        <Tooltip
          variant="bright"
          css={{ zIndex: zIndex("modalTooltip") }}
          content={disabledMessage}
          side="top"
        >
          {/*  span role none so that the disabled state of the menu link
               doesn't prevent the tooltip from opening */}
          <span role="none">{menuItem}</span>
        </Tooltip>
      );
    }

    return menuItem;
  }

  return (
    <button
      className={addRemoveItemStyle}
      onClick={() => {
        item.action?.();
        onClose();
      }}
    >
      {item.label}
    </button>
  );
};

const ADD_REVIEW_MENU_OFFSET_MINIMIZED = 16;
const ADD_REVIEW_MENU_OFFSET_EXPANDED = 13;

export const SidebarAddReviewMenu = () => {
  const [open, setOpen] = useState(false);
  const columns = useAddReviewMenuColumns();
  const { isNavMinimized } = useNavSidebarState();
  const modalRoot = useMemo(() => (IS_CLIENT ? document.getElementById(MODAL_ROOT_ID) : null), []);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      Analytics.addReviewMenuOpened();
    }
    setOpen(open);
  };

  const trigger = (
    <Button
      css={{ width: !isNavMinimized ? "100%" : "80%", marginTop: vars.space["1"] }}
      variant="secondary"
      size="small"
      aria-label="Add / Review"
    >
      {isNavMinimized && <Hamburger size={16} style={{ display: "block" }} />}
      {!isNavMinimized && "Add / Review"}
    </Button>
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal container={modalRoot}>
        <Popover.Content
          className={popoverContent}
          side="right"
          align="start"
          sideOffset={
            isNavMinimized ? ADD_REVIEW_MENU_OFFSET_MINIMIZED : ADD_REVIEW_MENU_OFFSET_EXPANDED
          }
        >
          {columns.map((col) => (
            <div className={column} key={col.title}>
              <div className={columnTitle}>{col.title}</div>
              {col.items
                .map((item) => (
                  <MenuItem key={item.label} item={item} onClose={() => setOpen(false)} />
                ))
                .filter(Boolean)}
            </div>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
